import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchCloudMaterials, fetchTemporaryMaterials, type MaterialFilters } from '../../../features/materials/api';
import { useToast } from '../../../hooks/useToast';
import { useMaterialsPanelStore } from '../../../stores/materialsPanelStore';
import { collectProductLinkedMaterials } from './productLinkedMaterials';
import { MaterialFolderCard } from './MaterialFolderCard';

export type MaterialFolder = 'shared' | 'personal';
export type SaveTarget = 'cloud' | 'temporary';

type Options = {
  /** この商品の ID (未確定なら undefined: 素材の集計を行わない) */
  productId: string | undefined;
  productType: 'amazon' | 'manual';
  /** 一括アップロードの対象 (表示中の商品画像の URL) */
  imageUrls: string[];
  /** 1 枚を共有フォルダへ保存する (ページ固有の引数はここで閉じ込める) */
  saveToCloud: (url: string) => Promise<unknown>;
  /** 1 枚を個人フォルダへ保存する */
  saveToTemporary: (url: string) => Promise<unknown>;
  /** ヘッダーのフォルダ別カードの文言 */
  card: {
    helpText: (folder: MaterialFolder) => string;
    countLabel: string;
    imageNoun: string;
  };
};

/**
 * この商品に紐付いた素材の集計 (フォルダ別の件数・アップロード済み判定) と、
 * ヘッダーのフォルダ別カード + 一括アップロード (Amazon / マニュアル共通)。
 * 計画: docs/requirements/product-detail-page-consolidation-plan.md 4.4
 */
export function useProductMaterialFolders({ productId, productType, imageUrls, saveToCloud, saveToTemporary, card }: Options) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const openWithProductFilter = useMaterialsPanelStore((s) => s.openWithProductFilter);
  // 一括アップロード中のフォルダ (null = 実行していない)
  const [bulkSavingTarget, setBulkSavingTarget] = useState<SaveTarget | null>(null);

  // この商品に紐付いた素材を全ページ集約し、件数 (バッジ) と source_url 集合 (アップロード済み判定) を
  // 商品スコープで取得する。Amazon 商品は material_product_links に加えて asin タグ付き素材も対象 (list_materials RPC)
  const materialLinksEnabled = !!productId;
  const productMaterialFilters: MaterialFilters = { product_id: productId ?? '', product_type: productType };
  const { data: sharedLinked } = useQuery({
    queryKey: ['product-linked-materials', 'cloud', productId],
    queryFn: () => collectProductLinkedMaterials(fetchCloudMaterials, productMaterialFilters),
    enabled: materialLinksEnabled,
    staleTime: 60 * 1000,
  });
  const { data: personalLinked } = useQuery({
    queryKey: ['product-linked-materials', 'temporary', productId],
    queryFn: () => collectProductLinkedMaterials(fetchTemporaryMaterials, productMaterialFilters),
    enabled: materialLinksEnabled,
    staleTime: 60 * 1000,
  });
  const sharedMaterialCount = sharedLinked?.count ?? 0;
  const personalMaterialCount = personalLinked?.count ?? 0;

  // アップロード済み判定は「この商品に紐付いた素材」の source_url 集合で行う (商品スコープ)。
  // 組織のどこかに同 URL があっても、この商品に未リンクなら未アップロード扱いとし、
  // アップロード (= 既存素材の再利用 + 商品リンク追加) を実行できるようにする
  const isImageSaved = (imageUrl: string) => sharedLinked?.sourceUrls.has(imageUrl) ?? false;
  const isImageSavedInTemporary = (imageUrl: string) => personalLinked?.sourceUrls.has(imageUrl) ?? false;

  // 指定フォルダにまだ保存されていない画像URLだけを返す（保存済みは除外）
  const pendingUploadUrls = (target: SaveTarget): string[] =>
    target === 'cloud'
      ? imageUrls.filter((url) => !isImageSaved(url))
      : imageUrls.filter((url) => !isImageSavedInTemporary(url));

  const handleBulkSave = async (target: SaveTarget) => {
    if (bulkSavingTarget) return;
    const targetUrls = pendingUploadUrls(target);
    if (targetUrls.length === 0) {
      toast.show('アップロードする画像がありません');
      return;
    }
    setBulkSavingTarget(target);
    let successCount = 0;
    let failCount = 0;
    for (const url of targetUrls) {
      try {
        if (target === 'cloud') {
          await saveToCloud(url);
        } else {
          await saveToTemporary(url);
        }
        successCount++;
      } catch {
        failCount++;
      }
    }
    setBulkSavingTarget(null);
    queryClient.invalidateQueries({ queryKey: target === 'cloud' ? ['cloud-materials'] : ['temporary-materials'] });
    queryClient.invalidateQueries({ queryKey: target === 'cloud' ? ['cloud-materials-all'] : ['temporary-materials-all'] });
    // ヘッダーのフォルダ別カード (件数バッジ + アップロード済み判定) を更新
    queryClient.invalidateQueries({ queryKey: ['product-linked-materials', target === 'cloud' ? 'cloud' : 'temporary', productId] });
    if (failCount === 0) {
      toast.success(`全${successCount}枚の画像を${target === 'cloud' ? '共有フォルダ' : '個人フォルダ'}にアップロードしました`);
    } else {
      toast.show(`${successCount}枚アップロード、${failCount}枚失敗しました`);
    }
  };

  // ヘッダーのフォルダ別カード (共有コンポーネント)。
  // 素材ページへ遷移せず、素材パネルを manage モードで該当タブ + 商品フィルタ付きで開く
  const renderFolderCard = (folder: MaterialFolder) => {
    const isShared = folder === 'shared';
    const target: SaveTarget = isShared ? 'cloud' : 'temporary';
    return (
      <MaterialFolderCard
        folder={folder}
        materialCount={isShared ? sharedMaterialCount : personalMaterialCount}
        totalImages={imageUrls.length}
        pendingCount={pendingUploadUrls(target).length}
        isUploading={bulkSavingTarget === target}
        uploadDisabled={bulkSavingTarget !== null}
        helpText={card.helpText(folder)}
        countLabel={card.countLabel}
        imageNoun={card.imageNoun}
        onUpload={() => handleBulkSave(target)}
        onOpen={() => openWithProductFilter(`${productType}:${productId}`, folder)}
      />
    );
  };

  return {
    materialLinksEnabled,
    sharedMaterialCount,
    personalMaterialCount,
    isImageSaved,
    isImageSavedInTemporary,
    pendingUploadUrls,
    bulkSavingTarget,
    handleBulkSave,
    renderFolderCard,
  };
}
